import withKeyboardFittingElement from 'HOCs/withKeyboardFittingElement';
import ProfileKYCIcon from 'assets/ver2/icons/profile-kyc-icon.png';
import PhotoCapturedBg from 'assets/ver2/images/photo-captured-bg.jpg';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import {
  DESKTOP_MIN_WIDTH,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
  THROTTLE_SUBMIT_SEC,
  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_NAME,
  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_NAME,
  VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_NAME,
  VERIFICATION_UPLOAD_DOCUMENTS_SELECT_DRIVER_LICENCE_SWITCH_NAME,
  VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_NAME,
} from 'constants';
import {
  PII_PROFILE_PERSONAL_DETAILS_DOB_NAME,
  PII_PROFILE_PERSONAL_DETAILS_GENDER_NAME,
  PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME,
  PII_PROFILE_PERSONAL_DETAILS_OCCUPATION_NAME,
  PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_FIELD,
  PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NAME_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NO_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_TYPE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_SWITCH_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_FIELD,
  PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME,
} from 'features/pii/const';
import { checkFormInputValidation } from 'features/pii/func';
import withFormProfileKYCModalInitial from 'features/profileKYC/HOCs/withFormProfileKYCModalInitial';
import FormEditOccupation from 'features/profileKYC/components/FormEditOccupation';
import FormEditPersonalDetails from 'features/profileKYC/components/FormEditPersonalDetails';
import FormEditResidentialAddress from 'features/profileKYC/components/FormEditResidentialAddress';
import useProfileKYC from 'features/profileKYC/hooks/useProfileKYC';
import useProfileKYCAPI from 'features/profileKYC/hooks/useProfileKYCAPI';
import useUploadDocumentsAPI from 'features/uploadDocuments/hooks/useUploadDocumentsAPI';
import useOccupation from 'hooks/occupation/useOccupation';
import useCommonStore from 'hooks/useCommonStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useThrottle from 'hooks/useThrottle';
import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import { paragraph500Mixin } from 'styles';

const FormProfileKYCPopup = withKeyboardFittingElement(
  withFormProfileKYCModalInitial(
    ({
      inputRef,
      isOpen,
      isBackdropOverlap,
      zIndexBackdropOverlap,
      zIndexModal = 0,
      information,
      setInformation,
      validation,
      setValidation,
      checkAllowSubmit,
    }) => {
      const { t } = useLang();
      const { isLoading, profileOccupation, setLoading } = useCommonStore();
      const { isDesktop } = useDetectDevice();

      const [currentEdit, setCurrentEdit] = useState(-1);

      const {
        handleShowProfileKYCSuccessModal,
        checkPersonalDetailsProfileOccupationChange,
      } = useProfileKYC();

      const formEditPersonalDetailsUploadDocumentsRef = useRef();
      const { isShowRestOfUploadDocuments } =
        formEditPersonalDetailsUploadDocumentsRef?.current || {};

      const formEditResidentialAddressRef = useRef();
      const currentProfileAddressUnit =
        information[PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME];

      const formEditPersonalDetailsOccupationRef = useRef();
      const { checkShowPreviousOccupationField } = useOccupation();
      const currentProfileOccupation =
        information[PII_PROFILE_PERSONAL_DETAILS_OCCUPATION_NAME];
      const currentProfilePrevOccupation =
        information[PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME];
      const isShowPreviousOccupationField = checkShowPreviousOccupationField(
        currentProfileOccupation?.key
      );
      const isPersonalDetailsProfileOccupationInformationChange =
        checkPersonalDetailsProfileOccupationChange(
          currentProfileOccupation?.key,
          profileOccupation?.job?.key
        );

      const isDriverLicenceSelected =
        information[
          VERIFICATION_UPLOAD_DOCUMENTS_SELECT_DRIVER_LICENCE_SWITCH_NAME
        ];

      const isEnterAddressManual = Boolean(
        information[PII_PROFILE_RESIDENTIAL_ADDRESS_SWITCH_NAME]
      );

      const isNextButtonDisabled =
        isLoading ||
        !checkAllowSubmit({
          information,
          validation,
          skipProps: [
            PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME,
            PII_PROFILE_PERSONAL_DETAILS_GENDER_NAME,
            PII_PROFILE_PERSONAL_DETAILS_DOB_NAME,
            VERIFICATION_UPLOAD_DOCUMENTS_SELECT_DRIVER_LICENCE_SWITCH_NAME,
            VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_NAME,
            PII_PROFILE_RESIDENTIAL_ADDRESS_SWITCH_NAME,
            ...(isShowRestOfUploadDocuments
              ? isDriverLicenceSelected
                ? [VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_NAME]
                : [
                    VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_NAME,
                    VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_NAME,
                  ]
              : [
                  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_NAME,
                  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_NAME,
                  VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_NAME,
                ]),
            ...(isPersonalDetailsProfileOccupationInformationChange
              ? [
                  ...(isShowPreviousOccupationField
                    ? [
                        checkFormInputValidation(
                          currentProfilePrevOccupation?.key,
                          PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_FIELD,
                          information,
                          validation
                        )?.[PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME]
                          ? []
                          : [
                              PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME,
                            ],
                      ]
                    : [PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME]),
                ]
              : [PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME]),
            ...(isEnterAddressManual
              ? [
                  !(
                    currentProfileAddressUnit &&
                    checkFormInputValidation(
                      currentProfileAddressUnit,
                      PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_FIELD,
                      information,
                      validation
                    )?.[PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME]
                  ) && PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME,
                ]
              : [
                  PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NO_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NAME_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_TYPE_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME,
                ]),
            ...(currentEdit !== 0
              ? [
                  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_NAME,
                  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_NAME,
                  VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_NAME,
                ]
              : []),
            ...(currentEdit !== 1
              ? [
                  PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NO_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NAME_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_TYPE_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME,
                  PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME,
                ]
              : []),
            ...(currentEdit !== 2
              ? [PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME]
              : []),
          ],
        });

      const { saveProfileDocumentsUploadRequest } = useUploadDocumentsAPI({
        information,
      });
      const { saveProfileKYCInformationRequest } = useProfileKYCAPI();
      const { throttle, isThrottleLocked, resetThrottle } =
        useThrottle(THROTTLE_SUBMIT_SEC);
      const handleOnSubmit = async () => {
        console.log(
          `⭐ FormProfileKYCPopup 👉 handleOnSubmit ➡️ information ↩`,
          information
        );
        console.log(
          `⭐ FormProfileKYCPopup 👉 handleOnSubmit ➡️ validation ↩`,
          validation
        );
        if (isThrottleLocked) return;
        if (isNextButtonDisabled) {
          resetThrottle();
          return;
        }
        setLoading(true);
        throttle(async () => {
          const profileInformation = {
            ...information,
            ...(isShowPreviousOccupationField
              ? {
                  [PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME]:
                    information?.[
                      PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME
                    ] || profileOccupation?.prevJob,
                }
              : {
                  [PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME]: '',
                }),
          };
          if (isShowRestOfUploadDocuments) {
            await saveProfileDocumentsUploadRequest({
              isConvertDataURLtoBlob: false,
              isContainDocumentSelfie: false,
              saveProfileInformationRequestOptions: {
                saveProfileInformationRequestCallback: (
                  documentUploadResponses
                ) =>
                  saveProfileKYCInformationRequest({
                    profileInformation,
                    documentUploadResponses,
                  }),
                saveProfileInformationRequestSuccessCallback:
                  handleShowProfileKYCSuccessModal,
              },
            });
            return;
          }
          const saveProfileKYCInformationDataResponse =
            await saveProfileKYCInformationRequest({
              profileInformation,
              errorCallback: resetThrottle,
            });
          const saveProfileKYCInformationData =
            saveProfileKYCInformationDataResponse?.data;
          if (!isEmpty(saveProfileKYCInformationData)) {
            handleShowProfileKYCSuccessModal();
            resetThrottle();
          }
        });
      };
      const renderModalFooter = () => {
        if (currentEdit !== -1) return null;
        if (isNextButtonDisabled) return null;
        return (
          <>
            <SizeBox height={isDesktop ? 140 : 0} />
            <ModalFooter>
              <ButtonCommon
                value={t('button_confirm')}
                color="var(--c-primary)"
                background="var(--bg-primary)"
                isFill={true}
                styles={{
                  margin: '0px',
                  marginInline: isDesktop ? 'auto' : 'unset',
                  width: isDesktop ? '300px' : '100%',
                }}
                onClick={handleOnSubmit}
                isUseKeyDown
              />
            </ModalFooter>
          </>
        );
      };

      return (
        <>
          <style>
            {zIndexModal
              ? `
            .form-profile-kyc-modal {
              z-index: ${zIndexModal || 1101};
            }
          `
              : ``}
            {isBackdropOverlap
              ? `
            .form-profile-kyc-modal-backdrop {
              z-index: ${zIndexBackdropOverlap || 1100}!important;
              background: #212121;
              opacity: 0.7!important;
              display: initial!important;
            }
          `
              : `
            .form-profile-kyc-modal-backdrop {
              background: #212121;
              opacity: 0.7!important;
              display: initial!important;
            }
          `}
          </style>
          <ModalStyled
            show={isOpen}
            onHide={() => {}}
            aria-labelledby="contained-modal-title-vcenter"
            className="form-profile-kyc-modal"
            backdropClassName="form-profile-kyc-modal-backdrop"
            centered
          >
            <ModalBody>
              <ProfileKYCIconStyled
                src={ProfileKYCIcon}
                width={70}
                height={70}
              />
              <SizeBox height={20} />
              <Desc>{t('form_profile_kyc_desc')}</Desc>
              <SizeBox height={10} />
              <FormEditSection>
                <FormEditPersonalDetails
                  ref={formEditPersonalDetailsUploadDocumentsRef}
                  inputRef={inputRef}
                  currentEdit={currentEdit}
                  setCurrentEdit={setCurrentEdit}
                  information={information}
                  setInformation={setInformation}
                  validation={validation}
                  setValidation={setValidation}
                  checkFormInputValidation={checkFormInputValidation}
                  isNextButtonDisabled={isNextButtonDisabled}
                />
                <SizeBox height={10} />
                <FormEditResidentialAddress
                  ref={formEditResidentialAddressRef}
                  inputRef={inputRef}
                  currentEdit={currentEdit}
                  setCurrentEdit={setCurrentEdit}
                  information={information}
                  setInformation={setInformation}
                  validation={validation}
                  setValidation={setValidation}
                  checkFormInputValidation={checkFormInputValidation}
                  isNextButtonDisabled={isNextButtonDisabled}
                />
                <SizeBox height={10} />
                <FormEditOccupation
                  ref={formEditPersonalDetailsOccupationRef}
                  inputRef={inputRef}
                  currentEdit={currentEdit}
                  setCurrentEdit={setCurrentEdit}
                  information={information}
                  setInformation={setInformation}
                  validation={validation}
                  setValidation={setValidation}
                  checkFormInputValidation={checkFormInputValidation}
                  isNextButtonDisabled={isNextButtonDisabled}
                />
              </FormEditSection>
            </ModalBody>
            {renderModalFooter()}
          </ModalStyled>
        </>
      );
    }
  )
);

const ModalStyled = styled(Modal)`
  margin: 0 !important;
  padding: 0 !important;
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100dvh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 20px 16px;
        border-radius: 0px;
        box-shadow: none;
        width: 100%;
        height: calc(100dvh - 70px);
        margin-inline: auto;
        border: none;
        border-radius: 0;
        background-image: url(${PhotoCapturedBg});
        background-position: top;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        overflow-y: auto;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      & .modal-content {
        padding: 20px;
        border-radius: 20px;
        box-shadow: var(--ds-bs-4);
        width: 476px;
        height: fit-content;
        margin-inline: auto;
        border: none;
        background-image: url(${PhotoCapturedBg});
        background-position: top;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        overflow-y: auto;
      }
    }
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  margin: 0;
`;
const FormEditSection = styled.div``;
const ProfileKYCIconStyled = styled.img`
  display: block;
  margin-inline: auto;
`;
const Desc = styled.p`
  ${paragraph500Mixin};
`;

const ModalFooter = styled(Modal.Footer)`
  border-top: none;
  position: fixed;
  bottom: 0;
  left: 0px;
  width: 100%;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    position: unset;
    padding-block: unset;
    padding-inline: unset;
    margin-inline: unset;
    border-top: none;
  }
`;

export default FormProfileKYCPopup;
